import { ChangeEvent, useCallback, useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  getPricebooks,
  readPricebookBindingRefusal,
  updateCustomer,
} from "../../../services";
import { PriceBookResponse } from "../../../types/price-book.type";

/** One request covers the whole catalogue; it holds a few dozen pricebooks. */
const PRICE_BOOK_LIMIT = 100;

interface PriceBookModalProps {
  open: boolean;
  customerUuid: string;
  customerName: string;
  customerCode: string;
  /** The pricebook code the customer is filed under now, or null when it has none. */
  priceBookCode: string | null;
  /** The shop's branch (`company_id`): only the pricebooks open in it are offered. */
  branchCompanyId: string | null;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Moves one customer onto another pricebook.
 *
 * `pricebook` is the single stored code; the API derives the per-billtype columns the
 * detail page also shows (สมุดราคาขายเงินสด / ใบแจ้งหนี้) from it on write, so this is
 * the only field an edit may touch.
 */
export default function PriceBookModal({
  open,
  customerUuid,
  customerName,
  customerCode,
  priceBookCode,
  branchCompanyId,
  onClose,
  onSaved,
}: PriceBookModalProps) {
  const [priceBooks, setPriceBooks] = useState<PriceBookResponse[]>([]);
  const [selectedCode, setSelectedCode] = useState<string>("");
  const [loadingList, setLoadingList] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [bindingRefusal, setBindingRefusal] = useState<string | null>(null);

  const loadPriceBooks = useCallback(async () => {
    setLoadingList(true);
    try {
      const {
        data: { data },
      } = await getPricebooks(PRICE_BOOK_LIMIT, branchCompanyId);
      // The API refuses to file a customer under a disabled pricebook, so offering one
      // would only produce a rejection the reader cannot act on.
      setPriceBooks(
        data.rows.filter(
          (priceBook: PriceBookResponse) => priceBook.status !== "disable",
        ),
      );
    } catch {
      // Reported by the axios interceptor. The list stays empty and the save is disabled.
      setPriceBooks([]);
    } finally {
      setLoadingList(false);
    }
  }, [branchCompanyId]);

  // Re-read on every open so a pricebook created or disabled meanwhile is reflected, and
  // reset the choice so a cancelled edit never leaks into the next one.
  useEffect(() => {
    if (!open) return;
    setSelectedCode(priceBookCode ?? "");
    setBindingRefusal(null);
    loadPriceBooks();
  }, [open, priceBookCode, loadPriceBooks]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedCode(event.target.value);
    setBindingRefusal(null);
  };

  const isUnchanged = selectedCode === (priceBookCode ?? "");
  const canSave = Boolean(selectedCode) && !isUnchanged && !loadingList;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await updateCustomer(customerUuid, { pricebook: selectedCode });
      onSaved();
      onClose();
    } catch (error) {
      // The API decides which books a shop may be put on; its refusal stays under the
      // select after the interceptor's toast has gone, so the reader can pick another.
      setBindingRefusal(readPricebookBindingRefusal(error));
    } finally {
      setSaving(false);
    }
  };

  // A code the catalogue page does not carry (disabled, or filed by the vansale sync)
  // still has to be selectable, or opening the dialog would silently clear it.
  const hasStoredCode = priceBooks.some(
    (priceBook) => priceBook.code === selectedCode,
  );

  const selectHelperText = loadingList
    ? "กำลังโหลดสมุดราคา..."
    : bindingRefusal ?? "แสดงเฉพาะสมุดราคาที่เปิดใช้ในสาขาของร้านค้านี้";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
        สมุดราคา
        <Typography variant="caption" color="text.secondary" display="block">
          {customerName} · {customerCode}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack gap={2}>
          <TextField
            select
            fullWidth
            label="สมุดราคา"
            value={selectedCode}
            onChange={handleChange}
            disabled={loadingList}
            error={Boolean(bindingRefusal)}
            helperText={selectHelperText}
          >
            {selectedCode && !hasStoredCode && (
              <MenuItem value={selectedCode}>{selectedCode}</MenuItem>
            )}
            {priceBooks.map((priceBook) => (
              <MenuItem key={priceBook.uuid} value={priceBook.code}>
                {priceBook.code} - {priceBook.name}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ display: "flex", gap: 1, color: "text.secondary" }}>
            <InfoOutlinedIcon fontSize="small" sx={{ mt: "2px" }} />
            <Typography variant="caption">
              ระบบจะตั้งสมุดราคาขายเงินสดและใบแจ้งหนี้ให้เองตามประเภทบิลของลูกค้ารายนี้
              มีผลกับบิลใบถัดไป บิลที่ออกไปแล้วไม่เปลี่ยน
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          ยกเลิก
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!canSave}
          loading={saving}
        >
          บันทึก
        </Button>
      </DialogActions>
    </Dialog>
  );
}
